<template>
  <b-modal
    ref="modal"
    :title="isEditing ? 'Editar artículo' : 'Nuevo artículo'"
    @hide="onHide"
    hide-footer
  >
    <b-form @submit.prevent="save" novalidate>
      <b-alert v-if="generalError" variant="danger" show class="mb-3">
        {{ generalError }}
      </b-alert>
      <b-form-group label="Codigo" label-for="code" :state="fieldState('code')">
        <b-form-input
          id="code"
          v-model="form.code"
          :state="fieldState('code')"
          required
          trim
        />
        <b-form-invalid-feedback v-if="fieldError('code')">
          {{ fieldError("code") }}
        </b-form-invalid-feedback>
      </b-form-group>
      <!-- Descripción -->
      <b-form-group
        label="Descripción"
        label-for="description"
        :state="fieldState('description')"
      >
        <b-form-input
          id="description"
          v-model="form.description"
          :state="fieldState('description')"
          required
          trim
        />
        <b-form-invalid-feedback v-if="fieldError('description')">
          {{ fieldError("description") }}
        </b-form-invalid-feedback>
      </b-form-group>

      <!-- Descripción del proveedor -->
      <b-form-group
        label="Descripción del proveedor"
        label-for="provider_description"
        :state="fieldState('provider_description')"
      >
        <b-form-input
          id="provider_description"
          v-model="form.provider_description"
          :state="fieldState('provider_description')"
        />
        <b-form-invalid-feedback v-if="fieldError('provider_description')">
          {{ fieldError("provider_description") }}
        </b-form-invalid-feedback>
      </b-form-group>

      <b-form-row>
        <b-col>
          <b-form-group
            label="Stock"
            label-for="stock"
            :state="fieldState('stock')"
          >
            <b-form-input
              id="stock"
              type="number"
              v-model.number="form.stock"
              :state="fieldState('stock')"
            />
            <b-form-invalid-feedback v-if="fieldError('stock')">
              {{ fieldError("stock") }}
            </b-form-invalid-feedback>
          </b-form-group>
        </b-col>

        <b-col>
          <b-form-group
            label="Stock mínimo"
            label-for="stock_min"
            :state="fieldState('stock_min')"
          >
            <b-form-input
              id="stock_min"
              type="number"
              v-model.number="form.stock_min"
              :state="fieldState('stock_min')"
            />
            <b-form-invalid-feedback v-if="fieldError('stock_min')">
              {{ fieldError("stock_min") }}
            </b-form-invalid-feedback>
          </b-form-group>
        </b-col>
      </b-form-row>

      <b-form-row>
        <b-col>
          <b-form-group
            label="Precio"
            label-for="price"
            :state="fieldState('price')"
          >
            <b-form-input
              id="price"
              type="number"
              step="0.01"
              v-model.number="form.price"
              :state="fieldState('price')"
            />
            <b-form-invalid-feedback v-if="fieldError('price')">
              {{ fieldError("price") }}
            </b-form-invalid-feedback>
          </b-form-group>
        </b-col>

        <b-col>
          <b-form-group
            label="Costo"
            label-for="cost"
            :state="fieldState('cost')"
          >
            <b-form-input
              id="cost"
              type="number"
              step="0.01"
              v-model.number="form.cost"
              :state="fieldState('cost')"
            />
            <b-form-invalid-feedback v-if="fieldError('cost')">
              {{ fieldError("cost") }}
            </b-form-invalid-feedback>
          </b-form-group>
        </b-col>
      </b-form-row>

      <!-- Selector de categoría -->
      <b-form-group
        label="Categoría"
        label-for="category_id"
        :state="fieldState('category_id')"
      >
        <b-form-select
          id="category_id"
          v-model="form.category_id"
          :options="categoryOptions"
          :state="fieldState('category_id')"
          placeholder="-- Seleccionar categoría --"
        />
        <b-form-invalid-feedback v-if="fieldError('category_id')">
          {{ fieldError("category_id") }}
        </b-form-invalid-feedback>
      </b-form-group>

      <!-- Upload de imagen -->
      <b-form-group label="Imagen (opcional)">
        <b-form-file
          v-model="file"
          accept="image/*"
          :state="fieldState('image')"
          browse-text="Elegir imagen"
          placeholder="No se seleccionó archivo..."
          @change="onFileChange"
        />
        <b-form-invalid-feedback v-if="fieldError('image')">
          {{ fieldError("image") }}
        </b-form-invalid-feedback>

        <!-- Preview de imagen -->
        <div v-if="imagePreview" class="mt-3 d-flex align-items-center">
          <img
            :src="imagePreview"
            alt="preview"
            style="
              max-height: 90px;
              max-width: 120px;
              object-fit: contain;
              border: 1px solid #e9ecef;
              padding: 4px;
              border-radius: 4px;
            "
          />
          <div class="ml-3">
            <div>
              <strong>{{ fileNamePreview }}</strong>
            </div>
            <b-button
              size="sm"
              variant="danger"
              class="mt-2"
              @click="removeImage"
              :disabled="loading"
              >Eliminar imagen</b-button
            >
          </div>
        </div>
      </b-form-group>

      <!-- Botones -->
      <b-row class="mt-3">
        <b-col cols="6">
          <b-button type="submit" variant="primary" :disabled="loading">
            <b-spinner small v-if="loading" label="Guardando..." class="mr-2" />
            Guardar
          </b-button>
          <b-button
            variant="secondary"
            class="ml-2"
            @click="hide()"
            :disabled="loading"
            >Cancelar</b-button
          >
        </b-col>
      </b-row>
    </b-form>
  </b-modal>
</template>

<script>
import ArticleService from "@/services/article.service";

export default {
  props: {
    article: { type: Object, default: null },
    categories: { type: Array, default: () => [] },
  },
  data() {
    return {
      form: this.article ? Object.assign({}, this.article) : this.empty(),
      errors: {},
      generalError: null,
      loading: false,
      file: null,
      imagePreview: this.article
        ? this.article.image_url || this.article.image
        : null,
    };
  },
  computed: {
    isEditing() {
      return !!(this.form && this.form.id);
    },
    categoryOptions() {
      return this.categories.map((c) => ({ value: c.id, text: c.name }));
    },
    fileNamePreview() {
      if (this.file && this.file.name) return this.file.name;
      if (this.form && this.form.image_name) return this.form.image_name;
      return "Imagen seleccionada";
    },
  },
  watch: {
    article(newVal) {
      this.form = newVal ? Object.assign({}, newVal) : this.empty();
      this.errors = {};
      this.generalError = null;

      if (newVal && newVal.files && newVal.files.length > 0) {
        const rawPath = newVal.files[0].path.replace("public/", "storage/");
        this.imagePreview = `${
          process.env.VUE_APP_API_URL || "http://vps-5436570-x.dattaweb.com:8080"
        }/${rawPath}`;
        this.form.image_name = newVal.files[0].name;
      } else {
        this.imagePreview = newVal?.image_url || newVal?.image || null;
      }

      this.file = null;
      delete this.form._remove_image;
    },
  },
  methods: {
    empty() {
      return {
        description: "",
        provider_description: "",
        stock: 0,
        stock_min: 0,
        price: 0,
        cost: 0,
        code: "",
        category_id: null,
        image_url: null,
        image_name: null,
      };
    },
    show() {
      this.errors = {};
      this.generalError = null;
      if (!this.article) {
        this.form = this.empty();
        this.imagePreview = null;
      } else if (this.article.files && this.article.files.length > 0) {
        const rawPath = this.article.files[0].path.replace(
          "public/",
          "storage/"
        );
        this.imagePreview = `${
          process.env.VUE_APP_API_URL || "http://vps-5436570-x.dattaweb.com:8080"
        }/${rawPath}`;
        this.form.image_name = this.article.files[0].name;
      } else {
        this.imagePreview =
          this.article.image_url || this.article.image || null;
      }
      this.$refs.modal.show();
    },
    hide() {
      this.$refs.modal.hide();
    },
    onHide() {
      if (!this.isEditing) {
        this.form = this.empty();
        this.file = null;
        this.imagePreview = null;
      }
      this.errors = {};
      this.generalError = null;
      this.$emit("closed");
    },
    fieldError(field) {
      if (!this.errors) return null;
      const e = this.errors[field];
      return Array.isArray(e) ? e.join(" ") : e || null;
    },
    fieldState(field) {
      if (!this.errors || Object.keys(this.errors).length === 0) return null;
      return this.errors[field] ? false : true;
    },
    onFileChange(fileList) {
      const f = Array.isArray(fileList) ? fileList[0] : fileList;

      if (!f || !(f instanceof File)) {
        this.file = null;
        this.imagePreview = null;
        return;
      }

      const MAX_BYTES = 3 * 1024 * 1024;
      if (f.size > MAX_BYTES) {
        this.errors.image = [
          "El archivo supera el tamaño máximo permitido (3 MB).",
        ];
        this.file = null;
        this.imagePreview = null;
        return;
      }

      this.file = f;
      delete this.form._remove_image; // ✅ si se carga nueva imagen, se cancela eliminación

      const reader = new FileReader();
      reader.onload = (e) => {
        this.imagePreview = e.target.result;
      };
      reader.readAsDataURL(f);
      if (this.errors.image) delete this.errors.image;
    },
    removeImage() {
      this.file = null;
      this.imagePreview = null;
      this.form._remove_image = true; // ✅ siempre marcar flag al borrar
    },
    async save() {
      this.loading = true;
      this.errors = {};
      this.generalError = null;

      try {
        let payload;
        console.log(this.form);

        if (this.file || this.form._remove_image) {
          console.log("entra?");

          payload = new FormData();
          payload.append("description", this.form.description);
          payload.append(
            "provider_description",
            this.form.provider_description
          );
          payload.append("stock", this.form.stock);
          payload.append("stock_min", this.form.stock_min);
          payload.append("price", this.form.price);
          payload.append("cost", this.form.cost);
          payload.append("code", this.form.code);
          payload.append("category_id", this.form.category_id);

          if (this.file) payload.append("image", this.file);
          if (this.form._remove_image) payload.append("_remove_image", "1");

          if (this.isEditing) payload.append("_method", "PUT");
        } else {
          payload = new FormData();
          payload.append("description", this.form.description);
          payload.append(
            "provider_description",
            this.form.provider_description
          );
          payload.append("stock", this.form.stock);
          payload.append("stock_min", this.form.stock_min);
          payload.append("price", this.form.price);
          payload.append("cost", this.form.cost);
          payload.append("code", this.form.code);
          payload.append("category_id", this.form.category_id);
        }
        console.log(payload);
        console.log("📦 Contenido del payload:");
        for (let [key, value] of payload.entries()) {
          console.log(`${key}:`, value);
        }
        let res;
        if (this.isEditing) {
          payload.append("_method", "PUT");
          res = await ArticleService.update(this.form.id, payload);
        } else {
          res = await ArticleService.create(payload);
        }

        this.$emit("saved", res.data);
        this.$refs.modal.hide();
      } catch (err) {
        if (err.response && err.response.status === 422) {
          this.errors = err.response.data.errors;
        } else {
          this.generalError = "Ocurrió un error al guardar.";
        }
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
/* ajustes visuales opcionales */
</style>
